/**
 * Раздел «Аукционы» (A3, план 2026-09-25 Часть 3): таблица, kanban по статусам,
 * импорт выгрузок МЭЛТОР. Карточка открывается в components/tender-inspector.js,
 * URL меняется на /tenders/:id (такие ссылки приходят в дайджесте Telegram).
 * Данные: /api/tenders (backend/app/api/tenders.py).
 */
import { api } from '../api.js';
import { canDo, getCurrentUser } from '../auth.js';
import { createDataTable } from '../components/data-table.js';
import { openModal } from '../components/modal.js';
import { showNotification } from '../components/notification.js';
import {
  currentTenderId, mountTenderInspector, openTenderInspector,
} from '../components/tender-inspector.js';
import {
  CLOSED_OTHER, DECISION_LABEL, FLOW, RECOMMENDATION_LABEL, STATUS_LABEL,
  apiErrorText, canTransition, cardName, deadlineSoon, eisUrl, esc, fmtDate, fmtDeadline, fmtMoney, isOwner,
  isPrefilterRejected, manualAllowed, prefilterBadge, roleAllows, statusText,
} from '../utils/tenders.js';

const MODE_KEY = 'ccpm_tenders_mode';
const MODES = [
  { key: 'table',   label: 'Таблица' },
  { key: 'kanban',  label: 'Kanban' },
  { key: 'imports', label: 'Импорты' },
];
const BOARD_LIMIT = 500;           // максимум limit в GET /api/tenders — открытые карточки доски
const BOARD_CLOSED_LIMIT = 100;    // К-2: в «Закрытые» — последние изменённые

let _root = null;
let _mode = 'table';
let _table = null;
let _board = null;                 // { items, openTotal, openShown, closedTotal, closedShown }
let _boardQuery = '';
let _boardError = null;
let _boardSearchTimer = null;
let _enrichBusy = false;
const _importLog = [];             // загрузки этой сессии браузера (истории импортов в API нет)

const pill = (cls, text) => `<span class="tdi-pill tdi-pill--${esc(cls)}">${esc(text)}</span>`;
const statusPill = (t) => pill(t.status, statusText(t));
/** Колонка «Отбор» (префильтр Руслана, Ф-1): Пропущен / Отсеян: причина / Проверить. */
const prefilterCell = (t) => {
  const b = prefilterBadge(t);
  return b ? `<span class="tdi-pill tdi-pill--${b.cls} dt-clip" title="${esc(b.title)}">${esc(b.text)}</span>` : '—';
};
// ТЕНД-ДИНАР-3: ячейка «Комментарии» — клик правит текст на месте. Кликабельный элемент —
// <a> без href: data-table игнорирует клики по ссылкам, поэтому карточка НЕ открывается.
const commentCell = (t) => {
  const text = (t.comment ?? '').trim();
  const inner = text
    ? `<span class="dt-clip">${esc(text)}</span>`
    : '<span class="tdi-comment-empty">добавить…</span>';
  return `<a class="tdi-comment" role="button" tabindex="0" data-comment-edit data-comment-id="${t.id}">${inner}</a>`;
};
const eisLink = (t) => {
  const url = eisUrl(t);
  return url ? `<a class="tdi-eis" href="${esc(url)}" target="_blank" rel="noopener" title="${t.eis_url ? 'Открыть в ЕИС' : 'Найти в ЕИС по номеру'}">ЕИС ↗</a>` : '';
};

function loadMode() {
  try { return localStorage.getItem(MODE_KEY) || 'table'; } catch { return 'table'; }
}
function saveMode(m) {
  try { localStorage.setItem(MODE_KEY, m); } catch { /* не критично */ }
}

export async function initTenders(container, tenderId) {
  _table = null;
  _board = null;
  _boardError = null;
  container.innerHTML = `
    <div class="tenders-view">
      <header class="tenders-header">
        <h2>Аукционы</h2>
        <div class="tenders-modes" role="tablist">
          ${MODES.map(m => `<button type="button" role="tab" class="tenders-mode" data-mode="${m.key}">${m.label}</button>`).join('')}
        </div>
        <span class="tenders-hint">Письма МЭЛТОР импортируются автоматически каждые 30 минут.</span>
      </header>
      <div class="tenders-body"></div>
    </div>`;
  _root = container.querySelector('.tenders-view');
  // Открытая панель сжимает таблицу, а не закрывает её (UI-2, п.12).
  _root.addEventListener('ccpm:inspector-resize', (e) => {
    _root.style.setProperty('--tdi-space', `${Math.round(e.detail.width)}px`);
  });
  mountTenderInspector(_root);
  _root.addEventListener('click', (e) => {
    const m = e.target.closest?.('[data-mode]');
    if (m) { e.preventDefault(); setMode(m.dataset.mode); }
  });

  // Ссылка с фильтром (виджет Dashboard) всегда открывает таблицу.
  const m = /[?&](status|deadline|prefilter)=/.test(location.search) ? 'table' : loadMode();
  setMode(MODES.some(x => x.key === m) ? m : 'table', true);
  if (tenderId) openCard(tenderId, { push: false });
}

function setMode(mode, force = false) {
  if (mode === _mode && !force && _root.querySelector('.tenders-body').children.length) return;
  _mode = mode;
  saveMode(mode);
  _root.querySelectorAll('.tenders-mode').forEach(b => {
    b.classList.toggle('is-active', b.dataset.mode === mode);
    b.setAttribute('aria-selected', String(b.dataset.mode === mode));
  });
  const body = _root.querySelector('.tenders-body');
  body.className = `tenders-body tenders-body--${mode}`;
  _table = null;
  if (mode === 'table') renderTable(body);
  else if (mode === 'kanban') loadBoard();
  else renderImports(body);
}

// ── Карточка и URL ───────────────────────────────────────────────────────────

function openCard(id, { push = true, tab } = {}) {
  const path = `/tenders/${id}`;
  if (push && location.pathname !== path) {
    if (currentTenderId()) history.replaceState(null, '', path);
    else history.pushState(null, '', path);
  }
  _table?.setSelected(id);
  openTenderInspector(id, {
    tab,
    onChange: () => refreshData(),
    onClose: () => {
      _table?.setSelected(null);
      if (/^\/tenders\/\d+$/.test(location.pathname)) history.replaceState(null, '', '/tenders');
    },
  });
}

function refreshData() {
  if (_mode === 'table') { _table?.reload(); refreshEnrichButton(); }
  else if (_mode === 'kanban') loadBoard();
}

// ── Таблица ──────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'id', label: '№', width: 64, align: 'right', sortKey: 'id', render: t => `<span class="tdi-mono" data-card-no>${esc(t.id)}</span>` },
  { key: 'reg_number', label: 'Номер закупки', width: 200, render: t => `<span class="tdi-mono">${esc(t.reg_number)}</span>${eisLink(t)}` },
  { key: 'submission_deadline', label: 'Срок подачи (МСК)', width: 195, sortKey: 'submission_deadline',
    render: t => `<span class="${deadlineSoon(t) ? 'tdi-soon' : ''}">${fmtDeadline(t)}</span>` },
  { key: 'title', label: 'Название', width: 340, title: t => t.title ?? '', render: t => `<span class="dt-clip">${esc(t.title ?? '—')}</span>` },
  { key: 'prefilter_result', label: 'Отбор', width: 200, title: t => prefilterBadge(t)?.title ?? '', render: prefilterCell },
  { key: 'customer_name', label: 'Заказчик', width: 220, title: t => t.customer_name ?? '',
    render: t => `<span class="dt-clip">${esc(t.customer_name ?? '—')}</span>` },
  // Р-1: субъект РФ (tenders.region), пусто — «—», никогда «РФ».
  { key: 'region', label: 'Регион', width: 150, title: t => t.region ?? '', render: t => `<span class="dt-clip">${esc(t.region || '—')}</span>` },
  { key: 'city', label: 'Город', width: 140, title: t => t.city ?? '', render: t => `<span class="dt-clip">${esc(t.city || '—')}</span>` },
  { key: 'work_class', label: 'ПИР/СМР', width: 80, render: t => esc(t.work_class ?? '—') },
  { key: 'initial_price', label: 'НМЦК', width: 130, align: 'right', sortKey: 'initial_price', render: t => fmtMoney(t.initial_price) },
  { key: 'estimated_cost', label: 'Расчётная стоимость', width: 130, align: 'right', render: t => fmtMoney(t.estimated_cost) },
  { key: 'price_1', label: 'Наша цена', width: 120, align: 'right', render: t => fmtMoney(t.price_1) },
  { key: 'recommendation', label: 'Рекомендация', width: 120,
    render: t => (t.recommendation ? pill(t.recommendation, RECOMMENDATION_LABEL[t.recommendation] ?? t.recommendation) : '—') },
  { key: 'decision', label: 'Решение', width: 100,
    render: t => (t.decision ? pill(t.decision, DECISION_LABEL[t.decision] ?? t.decision) : '—') },
  { key: 'status', label: 'Статус', width: 140, render: t => statusPill(t) },
  // ТЕНД-ДИНАР-3: свободный комментарий по торгам, правка прямо в ячейке (клик по тексту).
  { key: 'comment', label: 'Комментарии', width: 240, title: t => t.comment ?? '', render: commentCell },
  { key: 'auction_date', label: 'Дата торгов (МСК)', width: 195, render: t => fmtDate(t.auction_date, true) },
  { key: 'created_at', label: 'Добавлена', width: 130, sortKey: 'created_at', hidden: true, render: t => fmtDate(t.created_at, true) },
];

// Р-1: value «регион не определён» в фильтре region (на сервере — пустой tenders.region).
const REGION_NONE = 'none';

const FILTERS = [
  { key: 'status', label: 'Статус', multi: true, options: [
    { value: 'new,screened', label: 'Новые' },
    { value: 'decision', label: 'На решении' },
    { value: 'approved,docs,submitted,control', label: 'Подача' },
    { value: 'won,guarantee,contract', label: 'Выиграны' },
    { value: 'lost,customer_refused', label: 'Проиграли' },
  ] },
  { key: 'region_code', label: 'РБ/РТ', options: ['РБ', 'РТ', 'Другие'].map(v => ({ value: v, label: v })) },
  { key: 'region', label: 'Регион', type: 'list', multi: true, placeholder: 'Найти регион',
    emptyValue: REGION_NONE, emptyLabel: 'Не определён' },
  { key: 'work_class', label: 'Класс', options: ['ПИР', 'СМР'].map(v => ({ value: v, label: v })) },
  { key: 'deadline', label: 'Срок', options: [{ value: '2', label: 'подача ≤ 2 дн' }] },
  // Показывает только закрытые (в т.ч. «Просрочен» / «Ушёл из выгрузки»), перекрывает «кроме закрытых».
  { key: 'closed', label: 'Показать', options: [{ value: '1', label: 'Закрытые' }] },
  // Первичный отбор (Ф-1): «Отсеяно» показывает закрытые префильтром, «На проверку» — review.
  { key: 'prefilter', label: 'Отбор', options: [
    { value: 'reject', label: 'Отсеяно' },
    { value: 'review', label: 'На проверку' },
  ] },
];

// Чип «Проиграли» — закрытые статусы: при их выборе снимаем «кроме закрытых» (как у reject),
// иначе список пуст. contract сюда НЕ входит (чтобы не менять поведение чипа «Выиграны»).
const CLOSED_CHIP_STATUSES = new Set(['lost', 'customer_refused']);

// Отбор (колонка и чипы) видит только владелец: для остальных он автоматический (UI-2, п.8).
const OWNER_ONLY_KEYS = new Set(['prefilter_result', 'prefilter']);
const forRole = (list) => (isOwner() ? list : list.filter(x => !OWNER_ONLY_KEYS.has(x.key)));

/** Фильтры из ссылки (виджет Dashboard): /tenders?status=decision, ?deadline=2, ?prefilter=review. */
function initialFromUrl() {
  const qs = new URLSearchParams(location.search);
  const filters = {};
  if (qs.get('status')) filters.status = [qs.get('status')];
  if (qs.get('deadline')) filters.deadline = [qs.get('deadline')];
  if (qs.get('prefilter')) filters.prefilter = [qs.get('prefilter')];
  if (!Object.keys(filters).length) return null;
  history.replaceState(null, '', location.pathname);
  return { filters, hideClosed: true, q: '' };
}

/** Параметры GET /api/tenders и /facets из состояния таблицы (одни и те же — числа сходятся). */
function queryParams({ sort, filters, q, hideClosed }) {
  const params = { q, sort: sort || 'submission_deadline' };
  const pf = isOwner() ? filters.prefilter?.[0] : undefined;
  if (pf) params.prefilter = pf;
  // Значения чипов статуса склеены через запятую — проверяем закрытые статусы по отдельности.
  const hasClosedOutcome = (filters.status ?? []).some(
    v => v.split(',').some(s => CLOSED_CHIP_STATUSES.has(s)));
  if (filters.closed?.length) params.is_closed = true;
  else if (pf === 'reject') params.is_closed = undefined;  // отсеянные закрыты — «кроме закрытых» не применяем
  else if (hasClosedOutcome) params.is_closed = undefined;  // «Проиграли» — закрытые, показываем
  else if (hideClosed) params.is_closed = false;
  if (filters.status?.length) params.status = filters.status.join(',');
  if (filters.region_code?.[0]) params.region_code = filters.region_code[0];
  if (filters.region?.length) params.region = filters.region.join(',');
  if (filters.work_class?.[0]) params.work_class = filters.work_class[0];
  // «Подача ≤ N дней» — по эффективному сроку на сервере (срок без времени — до 23:59:59 МСК).
  if (filters.deadline?.length) params.deadline_days = Number(filters.deadline[0]) || 2;
  return params;
}

function fetchRows(query) {
  return api.tenders.list({ ...queryParams(query), limit: query.limit, offset: query.offset });
}

/** Числа у чипов и строка «Всего N, открытых M, закрытых K» (GET /api/tenders/facets). */
async function fetchFacets(query) {
  const params = queryParams(query);
  delete params.sort;
  const f = await api.tenders.facets(params);
  const sum = (group, value) => value.split(',').reduce((n, v) => n + (group[v] ?? 0), 0);
  const counts = {};
  for (const flt of FILTERS) {
    if (flt.type === 'list') {
      counts[flt.key] = Object.fromEntries(Object.entries(f.regions ?? {}).map(([k, n]) => [k || REGION_NONE, n]));
      continue;
    }
    counts[flt.key] = Object.fromEntries(flt.options.map(o => [o.value, {
      status: () => sum(f.status, o.value),
      region_code: () => f.region[o.value] ?? 0,
      work_class: () => f.work_class[o.value] ?? 0,
      deadline: () => f.deadline_soon,
      closed: () => f.closed,
      prefilter: () => f.prefilter[o.value] ?? 0,
    }[flt.key]?.() ?? null]));
  }
  return { counts, summary: `Всего ${f.open + f.closed}, открытых ${f.open}, закрытых ${f.closed}` };
}

function renderTable(body) {
  body.innerHTML = '<div class="tenders-table"></div>';
  _table = createDataTable(body.firstElementChild, {
    storageKey: 'ccpm_dt_tenders',
    columns: forRole(COLUMNS),
    filters: forRole(FILTERS),
    closedToggle: { label: 'кроме закрытых' },
    actions: isOwner(),
    search: { placeholder: 'Название, заказчик, номер закупки или №' },
    pageSizes: [50, 100, 200, 500],
    defaultSort: 'submission_deadline',
    initialState: initialFromUrl(),
    fetch: fetchRows,
    facets: fetchFacets,
    rowClass: t => [t.source_gone_at ? 'is-gone' : '', t.status === 'expired' || isPrefilterRejected(t) ? 'is-expired' : '',
      deadlineSoon(t) ? 'is-soon' : ''].filter(Boolean).join(' '),
    onRowClick: t => openCard(t.id),
    emptyHint: `Аукционов пока нет. Загрузите выгрузку МЭЛТОР во вкладке
      <a href="#" data-mode="imports">«Импорты»</a> или дождитесь письма.`,
  });
  const cur = currentTenderId();
  if (cur) _table.setSelected(cur);
  // ТЕНД-ДИНАР-3: клик по ячейке «Комментарии» → правка на месте (контейнер пересоздаётся при
  // каждом renderTable, слушатель не дублируется). Доступно всем с tender.read.
  body.firstElementChild.addEventListener('click', (e) => {
    const el = e.target.closest?.('[data-comment-edit]');
    if (!el) return;
    e.preventDefault();
    const td = el.closest('td');
    const row = _table?.getRow(Number(el.dataset.commentId));
    if (td && row) startCommentEdit(td, row);
  });
  if (isOwner()) {
    body.firstElementChild.addEventListener('click', (e) => {
      if (e.target.closest?.('[data-enrich-request]')) { e.preventDefault(); enrichRequestAll(); }
    });
    refreshEnrichButton();
  }
}

// ТЕНД-ДИНАР-3: правка комментария прямо в ячейке — textarea на общих токенах формы.
// Enter (без Shift) или потеря фокуса — сохранить; Esc — отмена. Обновляем только строку
// (updateRow), а не всю таблицу: иначе терялся бы фокус и позиция прокрутки.
function startCommentEdit(td, row) {
  if (td.querySelector('textarea')) return;  // уже редактируем
  const cur = row.comment ?? '';
  td.innerHTML = '<textarea class="ti-input tdi-comment-edit" rows="2" maxlength="4000"></textarea>';
  const ta = td.querySelector('textarea');
  ta.value = cur;  // через .value, чтобы не экранировать (и не ломать разметку) текстом комментария
  ta.focus();
  ta.setSelectionRange(ta.value.length, ta.value.length);
  let done = false;
  const finish = (updated) => { if (done) return; done = true; _table?.updateRow(updated); };
  const save = async () => {
    if (done) return;
    const val = ta.value.trim();
    if (val === (row.comment ?? '')) { finish(row); return; }
    done = true;
    try {
      const updated = await api.tenders.setComment(row.id, val || null);
      _table?.updateRow(updated);
    } catch (err) {
      if (err.message !== '401') showNotification(apiErrorText(err), 'error');
      _table?.updateRow(row);
    }
  };
  ta.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
    else if (e.key === 'Escape') { e.preventDefault(); finish(row); }
  });
  ta.addEventListener('blur', save);
}

// ── Сайт МЭЛТОР: «Запросить обогащение» (Д-1) ────────────────────────────────

function refreshEnrichButton() {
  const table = _table;
  if (!table) return;
  const title = 'Точные сроки и документы с сайта МЭЛТОР для открытых карточек без них. '
    + 'Обогащает worker на следующем проходе (раз в 30 минут).';
  table.setActions(`<button type="button" class="dt-btn" data-enrich-request title="${esc(title)}"${_enrichBusy ? ' disabled' : ''}>
    ${_enrichBusy ? 'Запрашиваю…' : 'Запросить обогащение'}</button>`);
}

async function enrichRequestAll() {
  if (_enrichBusy) return;
  const ok = await openModal({
    title: 'Запросить обогащение',
    message: 'Worker возьмёт открытые карточки без точного срока или документов первыми на следующем '
      + 'проходе: сроки со временем, ИНН, обеспечения и файлы документации с сайта МЭЛТОР. '
      + 'Отсеянные префильтром не обогащаются.',
    confirmLabel: 'Запросить',
  });
  if (!ok) return;
  _enrichBusy = true;
  refreshEnrichButton();
  try {
    const r = await api.tenders.enrichRequestAll();
    showNotification(r.requested ? `Запрошено карточек: ${r.requested}` : 'Все карточки уже в очереди или обогащены', 'info');
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  } finally {
    _enrichBusy = false;
  }
  _table?.reload();
  refreshEnrichButton();
}

// ── Kanban ───────────────────────────────────────────────────────────────────

// К-1: явный маппинг столбцов доски. `status` — статус, который столбец представляет и куда
// ведёт перенос В него (null у «Закрытых» — своя логика выбора итога). «Разложена» (screened)
// своего столбца не имеет: очередь раскладки показывается в «Новой». «Догрузить документы» и
// «На решении» — виртуальные столбцы одного статуса decision (incomplete / прочие рекомендации),
// поэтому перенос между ними ничего не меняет.
const BOARD_COLUMNS = [
  { id: 'new',      title: 'Новая',                status: 'new' },
  { id: 'to_docs',  title: 'Догрузить\nдокументы', status: 'decision' },
  { id: 'decision', title: 'На решении',           status: 'decision' },
  // ТЕНД-ДИНАР-3: «Контроль» — открытый столбец между «Подана» и «Выиграли».
  ...['approved', 'docs', 'submitted', 'control', 'won', 'guarantee', 'contract'].map(s => ({ id: s, title: STATUS_LABEL[s], status: s })),
  { id: 'closed',   title: 'Закрытые',             status: null },
];
const COL = Object.fromEntries(BOARD_COLUMNS.map(c => [c.id, c]));
const colStatus = (id) => COL[id]?.status ?? null;
const colTitle = (id) => (COL[id]?.title ?? id).replace('\n', ' ');

// Единая функция «столбец карточки» (для groups, drag/drop): screened → «Новая»,
// decision+incomplete → «Догрузить документы», остальной decision → «На решении».
function columnOf(t) {
  if (t.status === 'screened') return 'new';
  if (t.status === 'decision') return t.recommendation === 'incomplete' ? 'to_docs' : 'decision';
  if (t.status === 'control') return 'control';  // ТЕНД-ДИНАР-3: control не в FLOW — отдельная ветка
  return FLOW.includes(t.status) ? t.status : 'closed';
}

// На доске отказ раскладки (no_go) — метка «Отказ» в стиле «На грани»/«Участвовать».
const BOARD_REC_LABEL = { ...RECOMMENDATION_LABEL, no_go: 'Отказ' };

// Э-1: столбцы этапов ведения — перенос сюда открывает форму кнопки в карточке (ничего не шлёт).
const STAGE_HINT = {
  submitted: 'отметьте «Заявка подана»',
  won: 'внесите «Итог торгов»',
  guarantee: 'отметьте «Гарантия оформлена»',
  contract: 'отметьте «Контракт подписан»',
};

function closedTargets(from) {
  return CLOSED_OTHER.filter(s => canTransition(from, s));
}

function dropAllowed(from, col) {
  if (col === 'closed') return closedTargets(from).length > 0;
  const to = colStatus(col);
  return !!to && canTransition(from, to) && manualAllowed(from, to);
}

async function loadBoard() {
  const body = _root.querySelector('.tenders-body');
  if (!_board) body.innerHTML = '<div class="loading-spinner">Загрузка...</div>';
  try {
    // К-2: открытые и закрытые — отдельными запросами, иначе 500 мест занимают старые закрытые.
    const [open, closed] = await Promise.all([
      api.tenders.list({ is_closed: false, sort: 'submission_deadline', limit: BOARD_LIMIT, offset: 0 }),
      api.tenders.list({ is_closed: true, sort: '-updated_at', limit: BOARD_CLOSED_LIMIT, offset: 0 }),
    ]);
    _board = {
      items: [...open.items, ...closed.items],
      openTotal: open.total, openShown: open.items.length,
      closedTotal: closed.total, closedShown: closed.items.length,
    };
    if (_mode === 'kanban') renderBoard();
  } catch (e) {
    if (e.message === '401') return;
    body.innerHTML = `<div class="tenders-error">Ошибка загрузки: ${esc(apiErrorText(e))}</div>`;
  }
}

function boardItems() {
  const q = _boardQuery.trim().toLowerCase();
  if (!q) return _board.items;
  return _board.items.filter(t => [t.title, t.customer_name, t.reg_number].some(v => (v ?? '').toLowerCase().includes(q)));
}

function renderCard(t) {
  return `
    <article class="pipeline-card tenders-card${deadlineSoon(t) ? ' is-soon' : ''}" draggable="${canDo('tender.transition')}"
             data-tender-id="${t.id}" data-status="${esc(t.status)}">
      <div class="tenders-card-title">${esc(t.title ?? t.reg_number)}</div>
      <div class="pipeline-card-meta"><span class="tdi-mono" title="${esc(cardName(t))}">№${esc(t.id)}</span><span>${fmtMoney(t.initial_price)}</span>${t.region ? `<span title="${esc([t.region, t.city].filter(Boolean).join(', '))}">${esc(t.region)}</span>` : ''}
        ${t.work_class ? `<span>${esc(t.work_class)}</span>` : ''}</div>
      <div class="pipeline-card-tasks">подача ${fmtDate(t.submission_deadline)}
        ${t.recommendation ? pill(t.recommendation, BOARD_REC_LABEL[t.recommendation] ?? t.recommendation) : ''}
        ${columnOf(t) === 'closed' ? statusPill(t) : ''}</div>
    </article>`;
}

function renderBoard() {
  const body = _root.querySelector('.tenders-body');
  const items = boardItems();
  const groups = Object.fromEntries(BOARD_COLUMNS.map(c => [c.id, []]));
  items.forEach(t => groups[columnOf(t)].push(t));
  const hints = [];
  if (_board.openTotal > _board.openShown) hints.push(`Показаны ${_board.openShown} из ${_board.openTotal} открытых.`);
  if (_board.closedTotal > _board.closedShown) hints.push(`Показаны последние ${_board.closedShown} закрытых из ${_board.closedTotal}.`);
  const more = hints.length ? `<span class="tenders-hint" data-board-more>${hints.join(' ')}</span>` : '';
  body.innerHTML = `
    <div class="tenders-board-bar">
      <input type="search" class="dt-search" data-board-search placeholder="Название, заказчик или номер" value="${esc(_boardQuery)}">
      <span class="tenders-hint">Перетаскивайте карточки по разрешённым переходам. «На решении» и «Догрузить документы» ставит раскладка, решение (Участвуем/Отказ) — через Марата.</span>
      ${more}
    </div>
    <div class="tenders-board-error"${_boardError ? '' : ' hidden'} role="alert">${esc(_boardError ?? '')}</div>
    <div class="pipeline-grid tenders-grid">
      ${BOARD_COLUMNS.map(c => `
        <section class="pipeline-col" data-status="${c.id}">
          <header class="pipeline-col-header">
            <span class="pipeline-col-title">${esc(c.title).replace(/\n/g, '<br>')}</span>
            <span class="pipeline-col-count">${groups[c.id].length}</span>
          </header>
          <div class="pipeline-col-body" data-drop-target="${c.id}">
            ${groups[c.id].map(renderCard).join('') || '<div class="pipeline-col-empty">пусто</div>'}
          </div>
        </section>`).join('')}
    </div>`;
  bindBoard(body);
}

function setBoardError(msg) {
  _boardError = msg;
  const el = _root.querySelector('.tenders-board-error');
  if (!el) return;
  el.textContent = msg ?? '';
  el.hidden = !msg;
}

function bindBoard(body) {
  const grid = body.querySelector('.tenders-grid');
  let drag = null;

  body.querySelector('[data-board-search]').addEventListener('input', (e) => {
    _boardQuery = e.target.value;
    clearTimeout(_boardSearchTimer);
    _boardSearchTimer = setTimeout(() => {
      renderBoard();
      const inp = _root.querySelector('[data-board-search]');
      inp?.focus();
      inp?.setSelectionRange(inp.value.length, inp.value.length);
    }, 250);
  });

  grid.addEventListener('dragstart', (e) => {
    const card = e.target.closest('.tenders-card');
    if (!card) return;
    const id = Number(card.dataset.tenderId);
    const item = _board.items.find(x => x.id === id);
    const fromCol = item ? columnOf(item) : (FLOW.includes(card.dataset.status) ? card.dataset.status : 'closed');
    drag = { id, from: card.dataset.status, fromCol };
    card.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(drag.id));
    grid.querySelectorAll('.pipeline-col-body').forEach(t => {
      const target = t.dataset.dropTarget;
      if (target === fromCol) return;                                  // свой столбец
      if (colStatus(target) && colStatus(target) === drag.from) return; // тот же статус (decision ↔ «Догрузить документы»)
      t.classList.add(dropAllowed(drag.from, target) ? 'drop-ok' : 'drop-forbidden');
    });
  });
  grid.addEventListener('dragend', () => {
    grid.querySelectorAll('.dragging').forEach(c => c.classList.remove('dragging'));
    grid.querySelectorAll('.pipeline-col-body').forEach(t => t.classList.remove('drop-ok', 'drop-forbidden', 'drop-hover'));
    drag = null;
  });
  grid.addEventListener('dragover', (e) => {
    const t = e.target.closest('.pipeline-col-body');
    if (!t || !drag) return;
    e.preventDefault();   // принимаем и запрещённые — чтобы объяснить текстом, почему нельзя
    t.classList.add('drop-hover');
  });
  grid.addEventListener('dragleave', (e) => e.target.closest('.pipeline-col-body')?.classList.remove('drop-hover'));
  grid.addEventListener('drop', async (e) => {
    const t = e.target.closest('.pipeline-col-body');
    if (!t || !drag) return;
    e.preventDefault();
    const { id, from, fromCol } = drag;
    const col = t.dataset.dropTarget;
    if (col === fromCol) return;   // тот же столбец — ничего
    await moveCard(id, from, col);
  });
  grid.addEventListener('click', (e) => {
    const card = e.target.closest('.tenders-card');
    if (card && !card.classList.contains('dragging')) openCard(Number(card.dataset.tenderId));
  });
}

async function moveCard(id, from, col) {
  const t = _board.items.find(x => x.id === id);
  const to = colStatus(col);               // «Догрузить документы» и «На решении» → 'decision'; «Закрытые» → null
  // Перенос, не меняющий статус (decision ↔ «Догрузить документы») — ничего, без ошибки.
  if (col !== 'closed' && to && to === from) return;
  // «Догрузить документы» наполняет раскладка (incomplete) — вручную сюда нельзя (AUTO_ONLY).
  if (col === 'to_docs') {
    setBoardError(`«Догрузить документы» ставит раскладка автоматически: вручную ${cardName(t ?? { id })} не переводится.`);
    return;
  }
  if (col !== 'closed' && dropAllowed(from, col) && !roleAllows(from, to, getCurrentUser()?.role)) {
    setBoardError('Вернуть просроченную карточку в работу может только владелец.');
    return;
  }
  if (col !== 'closed' && to && canTransition(from, to) && !manualAllowed(from, to)) {
    setBoardError(`«${STATUS_LABEL[to]}» ставит раскладка автоматически: вручную ${cardName(t ?? { id })} не переводится.`);
    return;
  }
  if (!dropAllowed(from, col)) {
    setBoardError(`Переход «${STATUS_LABEL[from]}» → «${col === 'closed' ? 'Закрытые' : colTitle(col)}» запрещён.`);
    return;
  }
  // Э-1: этапы после «Участвуем» ведёт Динар кнопками в карточке. Перенос на доске в
  // submitted/won/guarantee/contract открывает форму кнопки во вкладке «Этапы» и сам
  // ничего не отправляет (вариант Б, как в Ц-0 для «Участвуем»).
  if (STAGE_HINT[col]) {
    setBoardError(null);
    openCard(id, { tab: 'stages' });
    showNotification(`${cardName(t ?? { id })}: ${STAGE_HINT[col]} во вкладке «Этапы».`, 'info');
    return;
  }
  let call;
  if (col === 'approved') {
    if (!canDo('tender.decide')) { setBoardError('Решение «Участвуем» принимает Марат или владелец.'); return; }
    // Ц-0: цену участия вводит человек вручную, рекомендованная — только подсказка. Вместо
    // быстрого окна с предзаполненной ценой открываем карточку на вкладке «Решение», где цена
    // пустая, есть проверка порядка цифр и подсказка. Само решение отсюда не отправляем.
    openCard(id, { tab: 'decision' });
    showNotification(`${cardName(t ?? { id })}: укажите цену участия во вкладке «Решение» и нажмите «Участвуем».`, 'info');
    return;
  } else if (col === 'closed') {
    const targets = closedTargets(from);
    const v = await openModal({
      title: 'Закрыть аукцион', confirmLabel: 'Закрыть', danger: true,
      fields: [
        { name: 'status', label: 'Итог', type: 'select', value: targets[0],
          options: targets.map(s => ({ value: s, label: s === 'rejected' ? 'Отказ от участия (решение)' : STATUS_LABEL[s] })) },
        { name: 'comment', label: 'Причина или комментарий', placeholder: 'для отказа обязательно' },
      ],
    });
    if (!v) return;
    if (v.status === 'rejected') {
      if (!canDo('tender.decide')) { setBoardError('Отказ от участия принимает Марат или владелец.'); return; }
      if (!v.comment?.trim()) { setBoardError('Для отказа нужна причина.'); return; }
      call = () => api.tenders.decision(id, { decision: 'no_go', comment: v.comment.trim() });
    } else {
      call = () => api.tenders.transition(id, { status: v.status, comment: v.comment ?? undefined });
    }
  } else {
    if (col === 'contract') {
      const ok = await openModal({ title: 'Контракт', message: 'Будет создан проект в очереди CCPM (queued).', confirmLabel: 'Создать проект' });
      if (!ok) return;
    }
    call = () => api.tenders.transition(id, { status: to });
  }
  try {
    await call();
    setBoardError(null);
    showNotification(`${cardName(t ?? { id })}: ${STATUS_LABEL[from]} → ${col === 'closed' ? 'закрыт' : STATUS_LABEL[to]}`, 'info');
  } catch (e) {
    if (e.message === '401') return;
    setBoardError(`${e.status === 409 ? 'Переход отклонён сервером: ' : 'Ошибка: '}${apiErrorText(e)}`);
  }
  await loadBoard();
}

// ── Импорты ──────────────────────────────────────────────────────────────────

function renderImportResult(r) {
  return `<li class="tenders-import-row">
    <span class="tdi-mono">${esc(r.time)}</span> <b>${esc(r.file)}</b>
    ${r.error ? `<span class="tenders-error">${esc(r.error)}</span>` : `
      ${r.src_subject ? `<span>рассылка «${esc(r.src_subject)}»</span>` : ''}
      <span>строк ${r.rows_total}</span>
      <span class="tenders-imp-add">добавлено ${r.added}</span>
      <span class="tenders-imp-chg">изменено ${r.changed}</span>
      <span class="tenders-imp-rm">ушло ${r.removed}</span>
      ${r.duplicate ? '<span class="tenders-hint">повтор: этот файл уже загружали</span>' : ''}`}
  </li>`;
}

function renderImports(body) {
  const allowed = canDo('tender.import');
  body.innerHTML = `
    <div class="tenders-imports">
      <section class="dash-widget">
        <header class="dash-widget-header"><h3>Загрузить выгрузку МЭЛТОР</h3></header>
        ${allowed ? `
        <form class="tenders-import-form" data-import-form>
          <input type="file" name="file" accept=".xlsx,.eml" required class="tenders-file">
          <button type="submit" class="ti-btn ti-btn-primary">Загрузить</button>
        </form>
        <p class="ti-hint">xlsx-экспорт с сайта МЭЛТОР или письмо-дайджест .eml. Повторная загрузка того же файла ничего не меняет.
          Поля решения и раскладки импорт не трогает.</p>
        <div class="tenders-import-result" data-import-result></div>` : '<p class="ti-hint">Нет права на импорт (tender.import).</p>'}
      </section>
      <section class="dash-widget">
        <header class="dash-widget-header"><h3>Загрузки</h3></header>
        <p class="ti-hint">История импортов на сервере пока не отдаётся API: здесь загрузки из этой вкладки браузера.
          Письма МЭЛТОР worker импортирует сам (журнал — в docs/OPERATIONS.md).</p>
        <ul class="tenders-import-log" data-import-log>${_importLog.map(renderImportResult).join('')}</ul>
      </section>
    </div>`;
  const form = body.querySelector('[data-import-form]');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const file = form.querySelector('input[type=file]').files?.[0];
    if (!file) return;
    const btn = form.querySelector('button');
    btn.disabled = true;
    btn.textContent = 'Загрузка...';
    const entry = { time: new Date().toLocaleTimeString('ru-RU'), file: file.name };
    try {
      Object.assign(entry, await api.tenders.importMeltor(file));
    } catch (err) {
      if (err.message === '401') return;
      entry.error = apiErrorText(err);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Загрузить';
    }
    _importLog.unshift(entry);
    const res = body.querySelector('[data-import-result]');
    if (res) res.innerHTML = `<ul class="tenders-import-log">${renderImportResult(entry)}</ul>`;
    const log = body.querySelector('[data-import-log]');
    if (log) log.innerHTML = _importLog.map(renderImportResult).join('');
    if (!entry.error) showNotification(`Импорт: добавлено ${entry.added}, изменено ${entry.changed}, ушло ${entry.removed}`, 'info');
  });
}
